import { BUSINESS_LANGUAGES } from 'src/common/constants';
import prepareURL from 'src/common/prepareURL';
import { injectTagsOpt } from 'src/pages/analytics/tag/api/tagApiSlice';
import { setAreasState } from 'src/reducers/areas/slice';
import apiSlice from 'src/reducers/services/api';

const basePath = 'business/{bid}/store/{sid}/area';

const areaApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		areas: builder.query({
			query: (args) => prepareURL(basePath, args),
			transformResponse: async (response, meta, arg) => {
				let { areas } = response;
				try {
					for (const area of areas) {
						let tables = area.tables;
						await injectTagsOpt({ items: tables, api: response._api, extraOptions: response._extraOptions });
					}
				} catch (error) {
					console.error('Error transforming area', error);
				}
				return areas;
			},
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(setAreasState(data));
				} catch (err) {
					console.error(err);
				}
			},
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({ type: 'Areas', id })), // Individual areas
							{ type: 'Areas', id: 'LIST' }, // Full areas list
					  ]
					: [{ type: 'Areas', id: 'LIST' }],
		}),
		getArea: builder.query({
			query: (inputs) => {
				const { args } = inputs;
				return prepareURL(basePath + '/{aid}', args);
			},
			transformResponse: (response) => {
				const { area } = response;

				const transformedArea = {
					area: [{ lng: BUSINESS_LANGUAGES[0].code, text: area.title }],
					bookable: area.bookable,
					size_sqm: area.size_sqm,
					availableOnline: area.online,
					internal_note: area.internal_note,
				};

				if (area?.priority) {
					transformedArea.bookingPriority = { value: area.priority, label: area.priority };
				}

				transformedArea.guestNotes = [];
				transformedArea.guestNotes.push({ lng: area.default_locale, text: area?.guest_note ?? '' });

				BUSINESS_LANGUAGES.slice(1).forEach(({ code }) => {
					const translationKeys = Object.keys(area?.translations || []);

					if (translationKeys.includes(code)) {
						transformedArea.area.push({ lng: code, text: area.translations[code].title || '' });
						transformedArea.guestNotes.push({ lng: code, text: area.translations[code].guest_note || '' });
					} else {
						if (area.default_locale !== code) transformedArea.area.push({ lng: code, text: '' });
						if (!area?.guest_note || area.default_locale !== code) transformedArea.guestNotes.push({ lng: code, text: '' });
					}
				});

				return transformedArea;
			},
			providesTags: (result, error, { args }) => [{ type: 'Areas', id: args.aid }],
		}),
		createArea: builder.mutation({
			query: (inputs) => {
				const {
					body,
					params: { args },
				} = inputs;

				return {
					url: prepareURL(basePath, args),
					method: 'POST',
					body,
				};
			},
			invalidatesTags: [{ type: 'Areas', id: 'LIST' }],
		}),
		editArea: builder.mutation({
			query: (inputs) => {
				const {
					body,
					params: { args },
				} = inputs;

				return {
					url: prepareURL(basePath + '/{aid}', args),
					method: 'PUT',
					body,
				};
			},
			invalidatesTags: (result, error, { params: { args } }) => [
				{ type: 'Areas', id: args.aid },
				{ type: 'Areas', id: 'LIST' },
			],
		}),
		deleteArea: builder.mutation({
			query: (args) => {
				return {
					url: prepareURL(basePath + '/{aid}', args),
					method: 'DELETE',
				};
			},
			invalidatesTags: (result, error, args) => [
				{ type: 'Areas', id: args.area_id },
				{ type: 'Areas', id: 'LIST' },
			],
		}),
		getAreaTable: builder.query({
			query: (inputs) => {
				const { args } = inputs;
				return prepareURL(basePath + '/{aid}/table/{tid}', args);
			},
			transformResponse: (response) => {
				const { table } = response;

				const transformedAreaTable = {
					table: [{ lng: BUSINESS_LANGUAGES[0].code, text: table.title }],
					min_seats: table.min_seats,
					max_seats: table.max_seats,
					bookable: table.bookable,
					internal_note: table.internal_note,
					availableOnline: table.online,
					bookingPriority: table?.priority,
					type: table?.type ?? null,
					rotation: table?.rotation ?? null,
					x: table?.x ?? null,
					y: table?.y ?? null,
				};

				BUSINESS_LANGUAGES.slice(1).forEach(({ code }) => {
					const translationKeys = Object.keys(table?.translations || []);

					if (translationKeys.includes(code)) {
						transformedAreaTable.table.push({ lng: code, text: table.translations[code].title || '' });
					} else {
						if (table.default_locale !== code) transformedAreaTable.table.push({ lng: code, text: '' });
					}
				});

				return transformedAreaTable;
			},
			providesTags: (result, error, args) => [
				{ type: 'Areas', id: args.aid },
				{ type: 'AreaTables', id: args.tid },
			],
		}),
		getAreaLayout: builder.query({
			query: (args) => {
				return prepareURL(basePath + '/{aid}/layout', args);
			},
			transformResponse: (response) => {
				return response;
			},
			providesTags: (result, error, args) => [
				{ type: 'Areas', id: args.aid },
				{ type: 'AreaTables', id: args.tid },
			],
		}),
		deleteAreaTempLayout: builder.mutation({
			query: (inputs) => {
				const { args } = inputs;
				return {
					url: prepareURL(basePath + '/{aid}/temp-layout', args),
					method: 'DELETE',
				};
			},
			invalidatesTags: (result, error, args) => [
				{ type: 'Areas', id: args.aid },
				{ type: 'AreaTables', id: args.tid },
			],
		}),
		createAreaTable: builder.mutation({
			query: (inputs) => {
				const {
					body,
					params: { args },
				} = inputs;

				return {
					url: prepareURL(basePath + '/{aid}/table', args),
					method: 'POST',
					body,
				};
			},
			invalidatesTags: (result, error, { params: { args } }) => [
				{ type: 'Areas', id: args.aid },
				{ type: 'Areas', id: 'LIST' },
			],
		}),
		editAreaTable: builder.mutation({
			query: (inputs) => {
				const {
					body,
					params: { args },
				} = inputs;

				return {
					url: prepareURL(basePath + '/{aid}/table/{tid}', args),
					method: 'PUT',
					body,
				};
			},
			invalidatesTags: (result, error, { params: { args } }) => [
				{ type: 'Areas', id: args.aid },
				{ type: 'AreaTables', id: args.tid },
			],
		}),
		editAreaLayout: builder.mutation({
			query: (inputs) => {
				const {
					body,
					params: { args },
				} = inputs;

				return {
					url: prepareURL(basePath + '/{aid}/layout', args),
					method: 'PUT',
					body,
				};
			},
			invalidatesTags: (result, error, { params: { args } }) => [
				{ type: 'Areas', id: args.aid },
				{ type: 'AreaTables', id: args.tid },
			],
		}),
		deleteAreaTable: builder.mutation({
			query: (inputs) => {
				return {
					url: prepareURL(basePath + '/{aid}/table/{tid}', inputs),
					method: 'DELETE',
				};
			},
			invalidatesTags: (result, error, { aid, tid }) => [
				{ type: 'Areas', id: aid },
				{ type: 'AreaTables', id: tid },
			],
		}),
		deleteAreaTables: builder.mutation({
			query: (inputs) => {
				const {
					body,
					params: { args },
				} = inputs;

				return {
					url: prepareURL(basePath + '/{aid}/table', args),
					method: 'DELETE',
					body,
				};
			},
			invalidatesTags: (result, error, { aid, tid }) => [
				{ type: 'Areas', id: aid },
				{ type: 'AreaTables', id: tid },
			],
		}),
	}),
});

export const {
	useAreasQuery,
	useGetAreaQuery,
	useCreateAreaMutation,
	useEditAreaMutation,
	useDeleteAreaMutation,
	useGetAreaTableQuery,
	useCreateAreaTableMutation,
	useEditAreaTableMutation,
	useDeleteAreaTableMutation,
	useDeleteAreaTablesMutation,
	useEditAreaLayoutMutation,
	useGetAreaLayoutQuery,
	useDeleteAreaTempLayoutMutation,
} = areaApiSlice;
